import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import VirtualClassRoom from '../components/VirtualClassRoom';
import ClassScheduleModal from '../components/ClassScheduleModal';
import { Video, Users, Play, Radio, Calendar, Clock, History, Plus, AlertCircle, CheckCircle, Maximize2, Minimize2 } from 'lucide-react';

const VirtualClassPage = () => {
    const { user } = useAuth();
    const isLecturer = user?.role === 'lecturer';

    const [activeTab, setActiveTab] = useState('live'); // 'live', 'upcoming', 'history'
    const [activeSession, setActiveSession] = useState(null); // The session user is currently IN
    const [isFullScreen, setIsFullScreen] = useState(true); // Default to full screen when joining

    // Data States
    const [liveSessions, setLiveSessions] = useState([]);
    const [upcomingSessions, setUpcomingSessions] = useState([]);
    const [historySessions, setHistorySessions] = useState([]);
    const [units, setUnits] = useState([]);

    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    useEffect(() => {
        fetchAllData();
    }, [activeTab]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            // Parallel fetching for efficiency
            const [upcomingRes, historyRes, unitsRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_BASE_URL}/livekit/sessions/upcoming`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/livekit/sessions/history`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                isLecturer ? fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }) : Promise.resolve({ json: () => [] })
            ]);

            const upcomingData = await upcomingRes.json();
            const historyData = await historyRes.json();

            // Separate Live (Ongoing) from Upcoming (Scheduled)
            setLiveSessions(upcomingData.filter(s => s.status === 'ongoing'));
            setUpcomingSessions(upcomingData.filter(s => s.status === 'scheduled'));
            setHistorySessions(historyData);

            if (isLecturer && unitsRes) {
                const unitsData = await unitsRes.json();
                setUnits(unitsData);
            }
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to load class data');
        } finally {
            setLoading(false);
        }
    };

    const handleScheduleClass = async (scheduleData) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/livekit/schedule-class`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(scheduleData)
            });

            if (response.ok) {
                alert('Class scheduled successfully!');
                fetchAllData(); // Refresh list
                setActiveTab('upcoming');
            } else {
                alert('Failed to schedule class');
            }
        } catch (err) {
            console.error(err);
            alert('Error scheduling class');
        }
    };

    const requestMediaPermissions = async () => {
        try {
            // Try to get both, but degrade gracefully if only one is available or if we just want to trigger the prompt
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true
            });
            // Immediately stop tracks to release hardware until LiveKit takes over
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (err) {
            console.error('Media permission denied or error:', err);
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                setError('Camera and Microphone access are required to join the virtual class. Please grant permissions in your browser settings.');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                setError('No camera or microphone found on this device.');
            } else {
                setError('Failed to access camera or microphone. Please check your device settings.');
            }
            return false;
        }
    };

    const handleJoinSession = async (unitId, sessionId = null) => {
        setLoading(true);
        setError('');

        // Aggressive permission check before joining
        const permissionsGranted = await requestMediaPermissions();
        if (!permissionsGranted) {
            setLoading(false);
            return;
        }

        try {
            const endpoint = isLecturer ? '/livekit/create-room' : '/livekit/join-room';
            const body = { unitId };
            if (sessionId) body.sessionId = sessionId; // Pass specific session ID if known

            // If starting a NEW class (no sessionId) and is Lecturer, ask for duration
            if (isLecturer && !sessionId) {
                const durationInput = prompt("Enter class duration in minutes (Max 90):", "90");
                if (durationInput === null) {
                    setLoading(false);
                    return; // Cancelled
                }
                body.duration = parseInt(durationInput);
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(body)
            });

            const data = await response.json();
            if (response.ok) {
                setToken(data.token);
                setActiveSession({
                    roomName: data.roomName,
                    sessionId: data.sessionId,
                    isHost: isLecturer,
                    startTime: data.startTime || new Date(),
                    duration: data.duration || 90
                });
                setIsFullScreen(true); // Default to full screen on join
            } else {
                setError(data.error || 'Failed to join class');
            }
        } catch (err) {
            setError('Error joining class');
        } finally {
            setLoading(false);
        }
    };

    const handleEndClass = async () => {
        if (!activeSession?.sessionId) return;
        if (!window.confirm('Are you sure you want to end this class for everyone?')) return;

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/livekit/end-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ sessionId: activeSession.sessionId })
            });
            handleDisconnect(null);
        } catch (err) {
            console.error('Error ending session:', err);
            alert('Failed to end session');
        }
    };

    const handleDisconnect = (reason) => {
        // Only show alert if it was an involuntary disconnect
        if (reason) {
            console.log('Disconnected reason:', reason);
            // Ignore standard client close or signal loss
            if (reason !== 'CLIENT_INITIATED_CLOSE' && reason !== 'signal connection lost') {
                alert('The class has ended or you were disconnected.');
            }
        }
        setToken(null);
        setActiveSession(null);
        setIsFullScreen(false);
        fetchAllData();
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateStr) => {
        return new Date(dateStr).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Timer Calculation
    const getRemainingTime = () => {
        if (!activeSession || !activeSession.startTime || !activeSession.duration) return null;

        const start = new Date(activeSession.startTime).getTime();
        const durationMs = activeSession.duration * 60 * 1000;
        const end = start + durationMs;
        const now = new Date().getTime();
        const remaining = end - now;

        if (remaining <= 0) return '00:00:00';

        const hours = Math.floor(remaining / (1000 * 60 * 60));
        const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const [timeLeft, setTimeLeft] = useState(getRemainingTime());

    useEffect(() => {
        if (!activeSession) return;
        const timer = setInterval(() => {
            const remaining = getRemainingTime();
            setTimeLeft(remaining);
        }, 1000);
        return () => clearInterval(timer);
    }, [activeSession]);

    if (activeSession && token) {
        const fullScreenStyles = {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '125vw',
            height: '125vh',
            zIndex: 99999,
            backgroundColor: '#111827', // gray-900
            margin: 0,
            padding: 0,
            overflow: 'hidden',
        };

        const stdStyles = {};

        const videoContent = (
            <div
                style={isFullScreen ? fullScreenStyles : stdStyles}
                className={`${!isFullScreen ? 'relative w-full h-[80vh] rounded-2xl overflow-hidden shadow-2xl border border-gray-200 bg-gray-900' : ''} transition-all duration-300`}
            >
                <div className="absolute top-4 left-4 z-[100000] flex gap-4 items-center">
                    <button
                        onClick={() => setIsFullScreen(!isFullScreen)}
                        className="bg-gray-800/80 backdrop-blur text-white p-2 rounded-lg hover:bg-gray-700 transition-colors border border-white/10"
                        title={isFullScreen ? "Minimize" : "Maximize"}
                    >
                        {isFullScreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                    </button>

                    {/* Timer Display */}
                    {timeLeft && (
                        <div className={`px-4 py-2 rounded-lg backdrop-blur border border-white/10 font-mono font-bold flex items-center gap-2 ${timeLeft === '00:00:00' ? 'bg-red-600/90 text-white animate-pulse' : 'bg-black/50 text-white'}`}>
                            <Clock className="w-4 h-4" />
                            {timeLeft}
                        </div>
                    )}

                    <button
                        onClick={() => handleDisconnect(null)}
                        className="bg-gray-600/80 backdrop-blur text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors border border-white/10 text-sm font-medium"
                    >
                        Leave Class
                    </button>
                    {isLecturer && (
                        <button
                            onClick={handleEndClass}
                            className="bg-red-600/90 backdrop-blur text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors shadow-lg shadow-red-900/20 text-sm font-medium"
                        >
                            End Class for All
                        </button>
                    )}
                </div>
                <VirtualClassRoom
                    token={token}
                    roomName={activeSession.roomName}
                    onDisconnect={handleDisconnect}
                    audioEnabled={isLecturer}
                />
            </div>
        );

        if (isFullScreen) {
            return ReactDOM.createPortal(videoContent, document.body);
        }

        return videoContent;
    }

    return (
        <div className="min-h-screen bg-gray-50/50 pb-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                            <span className="p-2 bg-blue-100 rounded-xl">
                                <Video className="w-8 h-8 text-blue-600" />
                            </span>
                            Virtual Classroom
                        </h1>
                        <p className="text-gray-500 mt-2 text-lg">Manage scheduling and join live lectures.</p>
                    </div>

                    {isLecturer && (
                        <button
                            onClick={() => setShowScheduleModal(true)}
                            className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 font-medium"
                        >
                            <Plus className="w-5 h-5" />
                            Schedule New Class
                        </button>
                    )}
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        {error}
                    </div>
                )}

                {/* Tabs */}
                <div className="flex border-b border-gray-200 mb-8 overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('live')}
                        className={`px-6 py-4 font-medium text-sm md:text-base whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'live' ? 'border-blue-600 text-blue-600 bg-blue-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <Radio className={`w-4 h-4 ${activeTab === 'live' ? 'animate-pulse' : ''}`} />
                        Live Now
                        {liveSessions.length > 0 && <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-bold">{liveSessions.length}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('upcoming')}
                        className={`px-6 py-4 font-medium text-sm md:text-base whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'upcoming' ? 'border-purple-600 text-purple-600 bg-purple-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <Calendar className="w-4 h-4" />
                        Upcoming
                        {upcomingSessions.length > 0 && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs font-bold">{upcomingSessions.length}</span>}
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`px-6 py-4 font-medium text-sm md:text-base whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'border-gray-600 text-gray-800 bg-gray-50/50' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                        <History className="w-4 h-4" />
                        History
                    </button>
                </div>

                {/* Content */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* LIVE TAB */}
                    {activeTab === 'live' && (
                        <div className="col-span-full space-y-8">
                            {/* LECTURER: START CLASS FOR UNITS */}
                            {isLecturer && (
                                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
                                    <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <Video className="w-5 h-5 text-blue-600" />
                                        Your Units
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {units.map(unit => {
                                            const activeSession = liveSessions.find(s => s.unitId === unit.id);
                                            return (
                                                <div key={unit.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-xl hover:bg-gray-50 transition-colors">
                                                    <div>
                                                        <h3 className="font-medium text-gray-900">{unit.name}</h3>
                                                        <p className="text-xs text-gray-500">{unit.code}</p>
                                                    </div>

                                                    {activeSession ? (
                                                        <button
                                                            onClick={() => handleJoinSession(unit.id, activeSession.id)}
                                                            className="px-4 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                                                        >
                                                            Rejoin
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleJoinSession(unit.id)}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm shadow-blue-100"
                                                        >
                                                            Start Class
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {units.length === 0 && <p className="text-gray-500 text-sm">No units assigned.</p>}
                                    </div>
                                </div>
                            )}

                            {/* LIVE SESSIONS SECTION */}
                            {(liveSessions.length > 0 || !isLecturer) && (
                                <div className="space-y-4">
                                    {liveSessions.length > 0 && <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                        <Radio className="w-5 h-5 text-red-500 animate-pulse" />
                                        Live Sessions
                                    </h2>}

                                    {liveSessions.length === 0 ? (
                                        <div className="py-12 text-center bg-white rounded-2xl border border-dashed border-gray-300">
                                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                                <Video className="w-8 h-8 text-gray-400" />
                                            </div>
                                            <h3 className="text-lg font-medium text-gray-900">No active classes</h3>
                                            <p className="text-gray-500">Scheduled classes will appear here when started.</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                            {liveSessions.map(session => (
                                                <div key={session.id} className="bg-white rounded-2xl p-6 shadow-sm border border-red-100 relative overflow-hidden group hover:shadow-md transition-shadow">
                                                    <div className="absolute top-0 right-0 p-3">
                                                        <span className="flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-600 text-xs font-bold rounded-full animate-pulse">
                                                            <span className="w-2 h-2 bg-red-600 rounded-full"></span> LIVE
                                                        </span>
                                                    </div>

                                                    <div className="mb-4 pr-16">
                                                        <h3 className="font-bold text-lg text-gray-900 line-clamp-1">{session.title || session.unit?.name}</h3>
                                                        <p className="text-gray-500 text-sm mt-1 line-clamp-1">{session.unit?.name}</p>
                                                    </div>

                                                    <div className="space-y-3 mb-6">
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <Users className="w-4 h-4 text-blue-500" />
                                                            Host: <span className="font-medium text-gray-900">{session.lecturer?.name}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                                            <Clock className="w-4 h-4 text-orange-500" />
                                                            Started: {formatTime(session.startTime)}
                                                            {session.duration && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full ml-1">{session.duration} mins</span>}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => handleJoinSession(session.unitId, session.id)}
                                                        className="w-full py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-red-100"
                                                    >
                                                        <Play className="w-5 h-5 fill-current" />
                                                        Rejoin Class
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* UPCOMING TAB */}
                    {activeTab === 'upcoming' && (
                        <>
                            {upcomingSessions.length === 0 ? (
                                <div className="col-span-full py-12 text-center bg-white rounded-2xl border border-dashed border-gray-300">
                                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Calendar className="w-8 h-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-lg font-medium text-gray-900">No upcoming classes</h3>
                                    <p className="text-gray-500">Scheduled classes will appear here.</p>
                                </div>
                            ) : (
                                upcomingSessions.map(session => (
                                    <div key={session.id} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:border-purple-200 transition-colors group">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-900 line-clamp-1">{session.title || session.unit?.name}</h3>
                                                <p className="text-purple-600 text-sm font-medium mt-1">{session.unit?.name}</p>
                                            </div>
                                            <div className="bg-purple-50 text-purple-700 px-3 py-1 rounded-lg text-xs font-bold uppercase">
                                                Scheduled
                                            </div>
                                        </div>

                                        <p className="text-gray-600 text-sm mb-6 line-clamp-2 min-h-[40px]">
                                            {session.description || 'No description provided for this session.'}
                                        </p>

                                        <div className="space-y-3 mb-6 bg-gray-50 p-4 rounded-xl">
                                            <div className="flex items-center gap-3 text-sm">
                                                <Calendar className="w-4 h-4 text-gray-500" />
                                                <span className="font-medium text-gray-900">{formatDate(session.scheduledStartTime)}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-sm">
                                                <Clock className="w-4 h-4 text-gray-500" />
                                                <span className="font-medium text-gray-900">{formatTime(session.scheduledStartTime)}</span>
                                            </div>
                                        </div>

                                        {isLecturer ? (
                                            <button
                                                onClick={() => handleJoinSession(session.unitId, session.id)}
                                                className="w-full py-2.5 border-2 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white rounded-xl font-bold transition-all"
                                            >
                                                Start Session
                                            </button>
                                        ) : (
                                            <button disabled className="w-full py-2.5 bg-gray-100 text-gray-400 rounded-xl font-medium cursor-not-allowed">
                                                Not Started Yet
                                            </button>
                                        )}
                                    </div>
                                ))
                            )}
                        </>
                    )}

                    {/* HISTORY TAB */}
                    {activeTab === 'history' && (
                        <div className="col-span-full">
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                                {historySessions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">No past sessions found.</p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Unit & Title</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Date</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Duration</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Lecturer</th>
                                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100">
                                            {historySessions.map(session => (
                                                <tr key={session.id} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <p className="font-medium text-gray-900">{session.title || 'Untitled Session'}</p>
                                                        <p className="text-xs text-gray-500">{session.unit?.name}</p>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {formatDate(session.startTime)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600 font-mono">
                                                        {session.duration ? `${session.duration} mins` : (session.endTime ?
                                                            `${Math.round((new Date(session.endTime) - new Date(session.startTime)) / 1000 / 60)} mins`
                                                            : '-')}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {session.lecturer?.name}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                            <CheckCircle className="w-3 h-3" /> Completed
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ClassScheduleModal
                isOpen={showScheduleModal}
                onClose={() => setShowScheduleModal(false)}
                units={units}
                onSchedule={handleScheduleClass}
            />
        </div>
    );
};

export default VirtualClassPage;
